import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  REVIEW_TEMPLATE_TOKEN_LABELS,
  createPlaceholderTokenHtml,
  ensureRichTextHtml,
  normalizeTemplateTokenName,
  sanitizeRichTextHtml,
} from "../utils/riskReviewTemplate";
import { colors, radius, shadows, spacing, typography } from "./tokens";

let tiptapModulesCache = null;

const resolveNamedExport = (moduleRef, namedKey) => moduleRef?.default || moduleRef?.[namedKey] || moduleRef;

const createPlaceholderTokenExtension = (Node, mergeAttributes) =>
  Node.create({
    name: "placeholderToken",
    inline: true,
    group: "inline",
    atom: true,
    selectable: true,

    addAttributes() {
      return {
        name: {
          default: "",
          parseHTML: (element) => normalizeTemplateTokenName(element.getAttribute("data-name")),
          renderHTML: (attributes) => ({
            "data-name": normalizeTemplateTokenName(attributes?.name),
          }),
        },
        label: {
          default: "",
          parseHTML: (element) => String(element?.textContent || "").trim(),
          renderHTML: () => ({}),
        },
      };
    },

    parseHTML() {
      return [{ tag: "span[data-name]" }];
    },

    renderHTML({ node, HTMLAttributes }) {
      const name = normalizeTemplateTokenName(node?.attrs?.name || HTMLAttributes?.["data-name"]);
      const label = String(node?.attrs?.label || REVIEW_TEMPLATE_TOKEN_LABELS[name] || name).trim();
      return [
        "span",
        mergeAttributes(
          {
            class: "template-token",
            "data-name": name,
            contenteditable: "false",
          },
          HTMLAttributes
        ),
        label,
      ];
    },

    renderText({ node }) {
      const name = normalizeTemplateTokenName(node?.attrs?.name);
      return name ? `{{${name}}}` : "";
    },

    addCommands() {
      return {
        insertPlaceholderToken:
          (attrs = {}) =>
          ({ chain }) => {
            const name = normalizeTemplateTokenName(attrs?.name);
            if (!name) return false;
            const label = String(attrs?.label || REVIEW_TEMPLATE_TOKEN_LABELS[name] || name).trim();
            return chain()
              .insertContent({
                type: this.name,
                attrs: { name, label },
              })
              .run();
          },
      };
    },

    addKeyboardShortcuts() {
      const removePlaceholderToken = (direction) => () => {
        const editor = this.editor;
        const selection = editor?.state?.selection;
        const selectedNode = selection?.node;
        if (selectedNode?.type?.name === this.name) {
          return editor.commands.deleteSelection();
        }
        if (!selection?.empty) return false;

        const cursor = selection.$from;
        const adjacentNode = direction === "backward" ? cursor.nodeBefore : cursor.nodeAfter;
        if (adjacentNode?.type?.name !== this.name) return false;

        const from = direction === "backward" ? cursor.pos - adjacentNode.nodeSize : cursor.pos;
        const to = direction === "backward" ? cursor.pos : cursor.pos + adjacentNode.nodeSize;
        const transaction = editor.state.tr.delete(from, to).scrollIntoView();
        editor.view.dispatch(transaction);
        return true;
      };

      return {
        Backspace: removePlaceholderToken("backward"),
        Delete: removePlaceholderToken("forward"),
      };
    },
  });

const loadTipTapModules = () => {
  if (process.env.NODE_ENV === "test") return null;
  if (tiptapModulesCache) return tiptapModulesCache;

  try {
    const reactModule = require("@tiptap/react");
    const coreModule = require("@tiptap/core");
    const starterModule = require("@tiptap/starter-kit");
    const underlineModule = require("@tiptap/extension-underline");

    const EditorContent = reactModule?.EditorContent;
    const useEditor = reactModule?.useEditor;
    const Node = coreModule?.Node;
    const mergeAttributes = coreModule?.mergeAttributes;
    const StarterKit = resolveNamedExport(starterModule, "StarterKit");
    const Underline = resolveNamedExport(underlineModule, "Underline");

    if (
      !EditorContent ||
      typeof useEditor !== "function" ||
      !Node ||
      typeof mergeAttributes !== "function" ||
      !StarterKit ||
      typeof StarterKit.configure !== "function" ||
      !Underline
    ) {
      return null;
    }

    const PlaceholderToken = createPlaceholderTokenExtension(Node, mergeAttributes);
    tiptapModulesCache = {
      EditorContent,
      useEditor,
      StarterKit,
      Underline,
      PlaceholderToken,
    };
    return tiptapModulesCache;
  } catch (_err) {
    return null;
  }
};

function normalizePlaceholderTokens(placeholderTokens) {
  return (placeholderTokens || [])
    .map((token) => {
      const name = normalizeTemplateTokenName(token?.name || token?.value || token);
      if (!name) return null;
      return {
        name,
        label: String(token?.label || REVIEW_TEMPLATE_TOKEN_LABELS[name] || name).trim(),
      };
    })
    .filter(Boolean);
}

const PLACEHOLDER_TOKEN_SELECTOR = "span[data-name]";

const syncPlaceholderTokenNodes = (host) => {
  if (!host) return;
  host.querySelectorAll(PLACEHOLDER_TOKEN_SELECTOR).forEach((token) => {
    token.setAttribute("contenteditable", "false");
  });
};

const createCaretRangeBeforeNode = (node) => {
  const parent = node?.parentNode;
  if (!parent) return null;
  const range = document.createRange();
  const offset = Array.prototype.indexOf.call(parent.childNodes, node);
  range.setStart(parent, Math.max(offset, 0));
  range.collapse(true);
  return range;
};

const rangesAreEqual = (left, right) =>
  left.compareBoundaryPoints(Range.START_TO_START, right) === 0 &&
  left.compareBoundaryPoints(Range.END_TO_END, right) === 0;

const findAdjacentPlaceholderToken = (host, range, direction) => {
  const tokens = host.querySelectorAll(PLACEHOLDER_TOKEN_SELECTOR);
  for (const token of tokens) {
    const probe = document.createRange();
    if (direction === "backward") {
      probe.setStartAfter(token);
    } else {
      probe.setStartBefore(token);
    }
    probe.collapse(true);
    if (rangesAreEqual(probe, range)) {
      return token;
    }
  }
  return null;
};

const getSelectedPlaceholderTokens = (host, range) =>
  Array.from(host.querySelectorAll(PLACEHOLDER_TOKEN_SELECTOR)).filter((token) => {
    try {
      return range.intersectsNode(token);
    } catch (_err) {
      return false;
    }
  });

const getPlaceholderTokenFromTarget = (target, host) => {
  if (!target || !host || typeof target.closest !== "function") return null;
  const token = target.closest(PLACEHOLDER_TOKEN_SELECTOR);
  return token && host.contains(token) ? token : null;
};

function ToolbarButton({ active, disabled, onClick, children, ariaLabel }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      style={{
        border: `1px solid ${active ? colors.brandPrimary : colors.borderStrong}`,
        borderRadius: radius.xs,
        background: active ? colors.accentSubtle : colors.surface,
        color: active ? colors.brandPrimary : colors.textPrimary,
        padding: `${spacing.xs / 2}px ${spacing.xs}px`,
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: typography.size.xs,
        fontWeight: typography.weight.medium,
        boxShadow: active ? shadows.xs : "none",
      }}
    >
      {children}
    </button>
  );
}

function EditorToolbar({
  disabled,
  boldActive,
  italicActive,
  underlineActive,
  bulletActive,
  placeholderTokens,
  onBold,
  onItalic,
  onUnderline,
  onBulletList,
  onInsertToken,
}) {
  const [tokenSelection, setTokenSelection] = useState("");

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: spacing.xs,
        flexWrap: "wrap",
        borderBottom: `1px solid ${colors.border}`,
        padding: spacing.xs,
        background: colors.surfaceSubtle,
      }}
    >
      <ToolbarButton ariaLabel="Bold" disabled={disabled} active={boldActive} onClick={onBold}>
        B
      </ToolbarButton>
      <ToolbarButton ariaLabel="Italic" disabled={disabled} active={italicActive} onClick={onItalic}>
        I
      </ToolbarButton>
      <ToolbarButton ariaLabel="Underline" disabled={disabled} active={underlineActive} onClick={onUnderline}>
        U
      </ToolbarButton>
      <ToolbarButton ariaLabel="Bullet List" disabled={disabled} active={bulletActive} onClick={onBulletList}>
        • List
      </ToolbarButton>
      {placeholderTokens.length > 0 && (
        <select
          aria-label="Insert Placeholder"
          value={tokenSelection}
          onChange={(event) => {
            const tokenName = event.target.value;
            setTokenSelection("");
            if (!tokenName) return;
            onInsertToken(tokenName);
          }}
          disabled={disabled}
          style={{
            border: `1px solid ${colors.borderStrong}`,
            borderRadius: radius.xs,
            padding: `${spacing.xs / 2}px ${spacing.xs}px`,
            fontSize: typography.size.xs,
            marginLeft: spacing.xs,
            background: colors.surface,
            color: colors.textPrimary,
          }}
        >
          <option value="">Insert Placeholder</option>
          {placeholderTokens.map((token) => (
            <option key={token.name} value={token.name}>
              {token.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function FallbackEditor({
  value,
  onChange,
  disabled,
  minHeight,
  normalizedPlaceholderTokens,
}) {
  const hostRef = useRef(null);
  const selectedTokenRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const normalizedValue = useMemo(() => ensureRichTextHtml(value), [value]);

  const insertHtmlAtCursor = (host, html) => {
    const selection = typeof window !== "undefined" ? window.getSelection?.() : null;
    if (!selection || selection.rangeCount === 0) {
      host.insertAdjacentHTML("beforeend", html);
      return;
    }
    const range = selection.getRangeAt(0);
    if (!host.contains(range.commonAncestorContainer)) {
      host.insertAdjacentHTML("beforeend", html);
      return;
    }
    range.deleteContents();
    const temp = document.createElement("div");
    temp.innerHTML = html;
    const fragment = document.createDocumentFragment();
    while (temp.firstChild) fragment.appendChild(temp.firstChild);
    const lastNode = fragment.lastChild;
    range.insertNode(fragment);
    if (lastNode) {
      const nextRange = document.createRange();
      nextRange.setStartAfter(lastNode);
      nextRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(nextRange);
    }
  };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (sanitizeRichTextHtml(host.innerHTML) === normalizedValue) return;
    host.innerHTML = normalizedValue;
    selectedTokenRef.current = null;
    syncPlaceholderTokenNodes(host);
  }, [normalizedValue]);

  const emitChange = (host) => {
    syncPlaceholderTokenNodes(host);
    onChange?.(sanitizeRichTextHtml(host.innerHTML) || "<p></p>");
  };

  const applyInlineCommand = (command) => {
    if (disabled) return;
    if (typeof document.execCommand === "function") {
      document.execCommand(command, false);
    }
    const host = hostRef.current;
    if (!host) return;
    selectedTokenRef.current = null;
    emitChange(host);
  };

  const insertToken = (tokenName) => {
    if (disabled) return;
    const name = normalizeTemplateTokenName(tokenName);
    if (!name) return;
    const host = hostRef.current;
    if (!host) return;
    const tokenHtml = createPlaceholderTokenHtml(name);
    if (typeof document.execCommand === "function") {
      document.execCommand("insertHTML", false, tokenHtml);
    } else {
      insertHtmlAtCursor(host, tokenHtml);
    }
    selectedTokenRef.current = null;
    emitChange(host);
  };

  const handleTokenMouseDown = useCallback((event) => {
    if (disabled) return;
    const host = hostRef.current;
    const token = getPlaceholderTokenFromTarget(event.target, host);
    if (!host) return;
    selectedTokenRef.current = token;
    if (!token) return;

    event.preventDefault();
    host.focus();
    const selection = window.getSelection?.();
    if (!selection) return;
    const range = document.createRange();
    range.selectNode(token);
    selection.removeAllRanges();
    selection.addRange(range);
  }, [disabled]);

  const handleTokenDeletion = useCallback((event) => {
    if (disabled || (event.key !== "Backspace" && event.key !== "Delete")) return;
    const host = hostRef.current;
    const selection = window.getSelection?.();
    if (!host) return;

    let tokensToRemove = [];
    let range = null;
    if (selection && selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
      if (host.contains(range.commonAncestorContainer)) {
        tokensToRemove = range.collapsed
          ? (() => {
              const token = findAdjacentPlaceholderToken(
                host,
                range,
                event.key === "Backspace" ? "backward" : "forward"
              );
              return token ? [token] : [];
            })()
          : getSelectedPlaceholderTokens(host, range);
      }
    }

    if (!tokensToRemove.length && selectedTokenRef.current && host.contains(selectedTokenRef.current)) {
      tokensToRemove = [selectedTokenRef.current];
    }

    if (!tokensToRemove.length) return;

    event.preventDefault();
    const nextRange = createCaretRangeBeforeNode(tokensToRemove[0]);
    tokensToRemove.forEach((token) => token.remove());
    host.normalize();
    selectedTokenRef.current = null;
    syncPlaceholderTokenNodes(host);
    if (nextRange && selection) {
      selection.removeAllRanges();
      selection.addRange(nextRange);
    }
    onChange?.(sanitizeRichTextHtml(host.innerHTML) || "<p></p>");
  }, [disabled, onChange]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    host.addEventListener("mousedown", handleTokenMouseDown);
    host.addEventListener("keydown", handleTokenDeletion);
    return () => {
      host.removeEventListener("mousedown", handleTokenMouseDown);
      host.removeEventListener("keydown", handleTokenDeletion);
    };
  }, [handleTokenDeletion, handleTokenMouseDown]);

  return (
    <div
      className={`rich-text-editor${disabled ? " rich-text-editor--disabled" : ""}`}
      style={{
        border: `1px solid ${isFocused ? colors.brandPrimary : colors.borderStrong}`,
        borderRadius: radius.sm,
        overflow: "hidden",
        background: disabled ? colors.surfaceSubtle : colors.surface,
        boxShadow: isFocused ? `0 0 0 3px ${colors.focusRing}` : "none",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
      }}
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={() => setIsFocused(false)}
    >
      <EditorToolbar
        disabled={disabled}
        boldActive={false}
        italicActive={false}
        underlineActive={false}
        bulletActive={false}
        placeholderTokens={normalizedPlaceholderTokens}
        onBold={() => applyInlineCommand("bold")}
        onItalic={() => applyInlineCommand("italic")}
        onUnderline={() => applyInlineCommand("underline")}
        onBulletList={() => applyInlineCommand("insertUnorderedList")}
        onInsertToken={insertToken}
      />
      <div
        ref={hostRef}
        className="rich-text-editor__content"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={(event) => {
          selectedTokenRef.current = null;
          emitChange(event.currentTarget);
        }}
        style={{
          minHeight,
          padding: spacing.sm,
          outline: "none",
          color: colors.textPrimary,
        }}
      />
    </div>
  );
}

function TipTapEditor({
  modules,
  value,
  onChange,
  disabled,
  minHeight,
  normalizedPlaceholderTokens,
}) {
  const { EditorContent, useEditor, StarterKit, Underline, PlaceholderToken } = modules;
  const normalizedValue = useMemo(() => ensureRichTextHtml(value), [value]);
  const [isFocused, setIsFocused] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        orderedList: false,
        strike: false,
        link: false,
        underline: false,
      }),
      Underline,
      PlaceholderToken,
    ],
    content: normalizedValue,
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: "rich-text-editor__content",
        style: `min-height:${minHeight}px;padding:${spacing.sm}px;outline:none;`,
      },
      handleDOMEvents: {
        focus: () => {
          setIsFocused(true);
          return false;
        },
        blur: () => {
          setIsFocused(false);
          return false;
        },
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      onChange?.(sanitizeRichTextHtml(currentEditor.getHTML()) || "<p></p>");
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = sanitizeRichTextHtml(editor.getHTML());
    if (current === normalizedValue) return;
    editor.commands.setContent(normalizedValue, false);
  }, [editor, normalizedValue]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  return (
    <div
      className={`rich-text-editor${disabled ? " rich-text-editor--disabled" : ""}`}
      style={{
        border: `1px solid ${isFocused ? colors.brandPrimary : colors.borderStrong}`,
        borderRadius: radius.sm,
        overflow: "hidden",
        background: disabled ? colors.surfaceSubtle : colors.surface,
        boxShadow: isFocused ? `0 0 0 3px ${colors.focusRing}` : "none",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
      }}
    >
      <EditorToolbar
        disabled={disabled || !editor}
        boldActive={Boolean(editor?.isActive("bold"))}
        italicActive={Boolean(editor?.isActive("italic"))}
        underlineActive={Boolean(editor?.isActive("underline"))}
        bulletActive={Boolean(editor?.isActive("bulletList"))}
        placeholderTokens={normalizedPlaceholderTokens}
        onBold={() => editor?.chain().focus().toggleBold().run()}
        onItalic={() => editor?.chain().focus().toggleItalic().run()}
        onUnderline={() => editor?.chain().focus().toggleUnderline().run()}
        onBulletList={() => editor?.chain().focus().toggleBulletList().run()}
        onInsertToken={(tokenName) => {
          const name = normalizeTemplateTokenName(tokenName);
          if (!name || !editor) return;
          editor
            .chain()
            .focus()
            .insertPlaceholderToken({
              name,
              label: REVIEW_TEMPLATE_TOKEN_LABELS[name] || name,
            })
            .run();
        }}
      />
      <EditorContent editor={editor} />
    </div>
  );
}

export default function RichTextEditor({
  value,
  onChange,
  disabled = false,
  minHeight = 120,
  placeholderTokens = [],
}) {
  const modules = loadTipTapModules();
  const normalizedPlaceholderTokens = useMemo(
    () => normalizePlaceholderTokens(placeholderTokens),
    [placeholderTokens]
  );

  if (!modules) {
    return (
      <FallbackEditor
        value={value}
        onChange={onChange}
        disabled={disabled}
        minHeight={minHeight}
        normalizedPlaceholderTokens={normalizedPlaceholderTokens}
      />
    );
  }

  return (
    <TipTapEditor
      modules={modules}
      value={value}
      onChange={onChange}
      disabled={disabled}
      minHeight={minHeight}
      normalizedPlaceholderTokens={normalizedPlaceholderTokens}
    />
  );
}
